import { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import PageTransition from '../../components/base/PageTransition';
import SEOHead from '../../components/base/SEOHead';

import OverviewContent from './components/OverviewContent';
import ArchitectureContent from './components/ArchitectureContent';
import AgentContent from './components/AgentContent';
import AgentLoopContent from './components/AgentLoopContent';
import WorkspaceContent from './components/WorkspaceContent';
import SessionContent from './components/SessionContent';
import MemoryContent from './components/MemoryContent';
import CompactionContent from './components/CompactionContent';
import MessagesContent from './components/MessagesContent';
import StreamingContent from './components/StreamingContent';
import ModelsContent from './components/ModelsContent';
import OAuthContent from './components/OAuthContent';
import ModelFailoverContent from './components/ModelFailoverContent';
import MultiAgentContent from './components/MultiAgentContent';

type SectionId =
  | 'overview'
  | 'architecture'
  | 'agent'
  | 'agent-loop'
  | 'workspace'
  | 'session'
  | 'memory'
  | 'compaction'
  | 'messages'
  | 'streaming'
  | 'models'
  | 'oauth'
  | 'model-failover'
  | 'multi-agent';

interface NavItem {
  id: SectionId;
  label: string;
  icon: string;
  group: string;
}

interface TocItem {
  id: string;
  label: string;
  level: number;
}

const navItems: NavItem[] = [
  { id: 'overview', label: '概念概览', icon: 'ri-layout-grid-line', group: '概览' },
  { id: 'architecture', label: '架构', icon: 'ri-router-line', group: '架构' },
  { id: 'agent', label: '智能体', icon: 'ri-robot-line', group: '智能体' },
  { id: 'agent-loop', label: '智能体循环', icon: 'ri-loop-left-line', group: '智能体' },
  { id: 'workspace', label: '工作区', icon: 'ri-folder-open-line', group: '智能体' },
  { id: 'session', label: '会话', icon: 'ri-chat-history-line', group: '会话与记忆' },
  { id: 'memory', label: '记忆', icon: 'ri-brain-line', group: '会话与记忆' },
  { id: 'compaction', label: '压缩', icon: 'ri-compress-line', group: '会话与记忆' },
  { id: 'messages', label: '消息', icon: 'ri-message-3-line', group: '消息与通道' },
  { id: 'streaming', label: '流式传输', icon: 'ri-live-line', group: '消息与通道' },
  { id: 'models', label: '模型', icon: 'ri-cpu-line', group: '模型' },
  { id: 'oauth', label: 'OAuth 认证', icon: 'ri-key-2-line', group: '模型' },
  { id: 'model-failover', label: '模型故障转移', icon: 'ri-swap-line', group: '模型' },
  { id: 'multi-agent', label: '多智能体路由', icon: 'ri-git-branch-line', group: '其他' },
];

const tocMap: Record<SectionId, TocItem[]> = {
  overview: [
    { id: 'ov-arch', label: '架构', level: 1 },
    { id: 'ov-agent', label: '智能体', level: 1 },
    { id: 'ov-session', label: '会话与记忆', level: 1 },
    { id: 'ov-message', label: '消息与通道', level: 1 },
    { id: 'ov-model', label: '模型', level: 1 },
    { id: 'ov-other', label: '其他', level: 1 },
  ],
  architecture: [
    { id: 'arch-gateway', label: 'Gateway 网关', level: 1 },
    { id: 'arch-components', label: '组件和流程', level: 1 },
    { id: 'arch-lifecycle', label: '连接生命周期', level: 1 },
    { id: 'arch-protocol', label: '线路协议摘要', level: 1 },
    { id: 'arch-pairing', label: '配对 + 本地信任', level: 1 },
    { id: 'arch-codegen', label: '协议类型和代码生成', level: 1 },
    { id: 'arch-remote', label: '远程访问', level: 1 },
    { id: 'arch-snapshot', label: '操作快照', level: 1 },
    { id: 'arch-invariants', label: '不变量', level: 1 },
  ],
  agent: [
    { id: 'agent-runtime', label: '智能体运行时', level: 1 },
    { id: 'agent-workspace', label: '工作区', level: 1 },
    { id: 'agent-bootstrap', label: '引导文件注入', level: 1 },
    { id: 'agent-tools', label: '内置工具', level: 1 },
    { id: 'agent-skills', label: 'Skills 系统', level: 1 },
    { id: 'agent-pimono', label: 'pi-mono 集成', level: 1 },
    { id: 'agent-session', label: '会话', level: 1 },
    { id: 'agent-streaming', label: '流式传输中的引导', level: 1 },
    { id: 'agent-model-ref', label: '模型引用', level: 1 },
    { id: 'agent-config', label: '配置', level: 1 },
  ],
  'agent-loop': [
    { id: 'loop-entry', label: '入口点', level: 1 },
    { id: 'loop-how', label: '工作原理', level: 1 },
    { id: 'loop-queue', label: '队列 + 并发', level: 1 },
    { id: 'loop-session-prep', label: '会话 + 工作区准备', level: 1 },
    { id: 'loop-prompt', label: '提示组装 + 系统提示', level: 1 },
    { id: 'loop-hooks', label: '钩子点', level: 1 },
    { id: 'loop-streaming', label: '流式传输 + 部分回复', level: 1 },
    { id: 'loop-tool-exec', label: '工具执行 + 消息工具', level: 1 },
    { id: 'loop-reply-shaping', label: '回复整形 + 抑制', level: 1 },
    { id: 'loop-compaction', label: '压缩 + 重试', level: 1 },
    { id: 'loop-events', label: '事件流', level: 1 },
    { id: 'loop-chat-channel', label: '聊天渠道处理', level: 1 },
    { id: 'loop-timeout', label: '超时', level: 1 },
    { id: 'loop-early-exit', label: '可能提前结束的情况', level: 1 },
  ],
  workspace: [
    { id: 'ws-default', label: '默认位置', level: 1 },
    { id: 'ws-extra', label: '额外的工作区文件夹', level: 1 },
    { id: 'ws-mapping', label: '工作区文件映射', level: 1 },
    { id: 'ws-excluded', label: '不包含的内容', level: 1 },
    { id: 'ws-git', label: 'Git 备份', level: 1 },
    { id: 'ws-no-secrets', label: '不要提交密钥', level: 1 },
    { id: 'ws-migrate', label: '迁移到新机器', level: 1 },
    { id: 'ws-advanced', label: '高级注意事项', level: 1 },
  ],
  session: [
    { id: 'sess-gateway', label: 'Gateway 是唯一数据源', level: 1 },
    { id: 'sess-storage', label: '状态存储位置', level: 1 },
    { id: 'sess-trim', label: '会话修剪', level: 1 },
    { id: 'sess-memory-flush', label: '压缩前记忆刷新', level: 1 },
    { id: 'sess-key-mapping', label: '传输到会话键的映射', level: 1 },
    { id: 'sess-lifecycle', label: '生命周期', level: 1 },
    { id: 'sess-send-policy', label: '发送策略', level: 1 },
    { id: 'sess-config', label: '配置示例', level: 1 },
    { id: 'sess-inspect', label: '检查', level: 1 },
    { id: 'sess-tips', label: '提示', level: 1 },
    { id: 'sess-source-meta', label: '会话来源元数据', level: 1 },
  ],
  memory: [
    { id: 'mem-file', label: '记忆文件（Markdown）', level: 1 },
    { id: 'mem-when-write', label: '何时写入记忆', level: 1 },
    { id: 'mem-auto-refresh', label: '自动记忆刷新', level: 1 },
    { id: 'mem-vector', label: '向量记忆搜索', level: 1 },
    { id: 'mem-extra-paths', label: '额外记忆路径', level: 2 },
    { id: 'mem-gemini', label: 'Gemini 嵌入', level: 2 },
    { id: 'mem-hybrid', label: '混合搜索 BM25+向量', level: 2 },
    { id: 'mem-embed-cache', label: '嵌入缓存', level: 2 },
    { id: 'mem-session-search', label: '会话记忆搜索', level: 2 },
    { id: 'mem-sqlite', label: 'SQLite 向量加速', level: 2 },
    { id: 'mem-local-embed', label: '本地嵌入自动下载', level: 2 },
    { id: 'mem-custom-endpoint', label: '自定义 OpenAI 兼容端点', level: 2 },
  ],
  compaction: [
    { id: 'comp-what', label: '什么是压缩', level: 1 },
    { id: 'comp-config', label: '配置', level: 1 },
    { id: 'comp-auto', label: '自动压缩', level: 1 },
    { id: 'comp-manual', label: '手动压缩', level: 1 },
    { id: 'comp-context-source', label: '上下文窗口来源', level: 1 },
    { id: 'comp-vs-trim', label: '压缩与修剪', level: 1 },
    { id: 'comp-tips', label: '提示', level: 1 },
  ],
  messages: [
    { id: 'msg-flow', label: '消息流程', level: 1 },
    { id: 'msg-dedup', label: '入站去重', level: 1 },
    { id: 'msg-debounce', label: '入站防抖', level: 1 },
    { id: 'msg-session-device', label: '会话和设备', level: 1 },
    { id: 'msg-body-history', label: '入站正文和历史上下文', level: 1 },
    { id: 'msg-queue', label: '队列和后续消息', level: 1 },
    { id: 'msg-streaming', label: '流式传输 / 分块和批处理', level: 1 },
    { id: 'msg-reasoning', label: '推理可见性和 token', level: 1 },
    { id: 'msg-prefix-thread', label: '前缀 / 线程和回复', level: 1 },
  ],
  streaming: [
    { id: 'stream-chunked', label: '分块流式传输', level: 1 },
    { id: 'stream-algo', label: '分块算法（低/高边界）', level: 1 },
    { id: 'stream-merge', label: '合并（合并流式块）', level: 1 },
    { id: 'stream-rhythm', label: '块之间的类人节奏', level: 1 },
    { id: 'stream-or-all', label: '"流式传输块或全部内容"', level: 1 },
    { id: 'stream-telegram', label: 'Telegram 草稿流式传输', level: 1 },
  ],
  models: [
    { id: 'model-how', label: '模型选择工作原理', level: 1 },
    { id: 'model-recommend', label: '快速模型推荐', level: 1 },
    { id: 'model-wizard', label: '设置向导', level: 1 },
    { id: 'model-config-keys', label: '配置键（概述）', level: 1 },
    { id: 'model-not-allowed', label: 'Model is not allowed', level: 1 },
    { id: 'model-switch', label: '在聊天中切换模型', level: 1 },
    { id: 'model-cli', label: 'CLI 命令', level: 1 },
    { id: 'model-scan', label: '扫描（OpenRouter 免费模型）', level: 1 },
    { id: 'model-registry', label: '模型注册表', level: 1 },
  ],
  oauth: [
    { id: 'oauth-why', label: '令牌汇聚点', level: 1 },
    { id: 'oauth-storage', label: '存储（令牌存放位置）', level: 1 },
    { id: 'oauth-anthropic', label: 'Anthropic setup-token', level: 1 },
    { id: 'oauth-exchange', label: 'OAuth 交换（登录工作原理）', level: 1 },
    { id: 'oauth-refresh', label: '刷新 + 过期', level: 1 },
    { id: 'oauth-multi', label: '多账户（配置文件）+ 路由', level: 1 },
  ],
  'model-failover': [
    { id: 'failover-auth', label: '认证存储（密钥 + OAuth）', level: 1 },
    { id: 'failover-profile', label: '配置文件 ID', level: 1 },
    { id: 'failover-rotation', label: '轮换顺序', level: 1 },
    { id: 'failover-cooldown', label: '冷却时间', level: 1 },
    { id: 'failover-billing', label: '账单禁用', level: 1 },
    { id: 'failover-model-fallback', label: '模型回退', level: 1 },
    { id: 'failover-config', label: '相关配置', level: 1 },
  ],
  'multi-agent': [
    { id: 'multi-what', label: '什么是"一个智能体"', level: 1 },
    { id: 'multi-paths', label: '路径（快速映射）', level: 1 },
    { id: 'multi-assistant', label: '智能体助手', level: 1 },
    { id: 'multi-personalities', label: '多个智能体 = 多种人格', level: 1 },
    { id: 'multi-whatsapp-split', label: '一个号码多个人', level: 1 },
    { id: 'multi-routing-rules', label: '路由规则', level: 1 },
    { id: 'multi-accounts', label: '多账户 / 电话号码', level: 1 },
    { id: 'multi-concepts', label: '概念', level: 1 },
    { id: 'multi-example1', label: '示例：两个 WhatsApp', level: 2 },
    { id: 'multi-example2', label: '示例：WhatsApp + Telegram', level: 2 },
    { id: 'multi-example3', label: '示例：同一渠道到 Opus', level: 2 },
    { id: 'multi-family', label: '家庭智能体', level: 2 },
    { id: 'multi-sandbox', label: '每智能体沙箱和工具配置', level: 1 },
  ],
};

const contentMap: Record<SectionId, React.ReactNode> = {
  overview: <OverviewContent />,
  architecture: <ArchitectureContent />,
  agent: <AgentContent />,
  'agent-loop': <AgentLoopContent />,
  workspace: <WorkspaceContent />,
  session: <SessionContent />,
  memory: <MemoryContent />,
  compaction: <CompactionContent />,
  messages: <MessagesContent />,
  streaming: <StreamingContent />,
  models: <ModelsContent />,
  oauth: <OAuthContent />,
  'model-failover': <ModelFailoverContent />,
  'multi-agent': <MultiAgentContent />,
};

const groups = ['概览', '架构', '智能体', '会话与记忆', '消息与通道', '模型', '其他'];

export default function AgentsPage() {
  const [activeSection, setActiveSection] = useState<SectionId>(() => {
    const saved = localStorage.getItem('agents_active_section');
    return (saved as SectionId) || 'overview';
  });
  const [activeToc, setActiveToc] = useState<string>('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const currentToc = tocMap[activeSection];
  const currentNavItem = navItems.find(item => item.id === activeSection);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://openclaw.cc';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: '智能体概念文档',
    description: 'OpenClaw 智能体架构、会话、记忆、模型等核心概念详解',
    url: `${siteUrl}/agents`,
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    author: {
      '@type': 'Organization',
      name: 'OpenClaw',
    },
    publisher: {
      '@type': 'Organization',
      name: 'OpenClaw',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/lobster.svg`,
      },
    },
    inLanguage: 'zh-CN',
  };

  // 监听导航栏子菜单点击
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'agents_active_section' && e.newValue) {
        setActiveSection(e.newValue as SectionId);
      }
    };

    const handleNavChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.storageKey === 'agents_active_section') {
        setActiveSection(detail.key as SectionId);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('nav-section-change', handleNavChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('nav-section-change', handleNavChange);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('agents_active_section', activeSection);
    setActiveToc(currentToc[0]?.id || '');
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeSection]);

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;
    const handleScroll = () => {
      const headings = currentToc
        .map((t) => document.getElementById(t.id))
        .filter(Boolean) as HTMLElement[];
      let current = headings[0]?.id || '';
      for (const el of headings) {
        if (el.getBoundingClientRect().top < 120) {
          current = el.id;
        }
      }
      setActiveToc(current);
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [activeSection, currentToc]);

  const handleSectionChange = (e: React.MouseEvent, id: SectionId) => {
    e.preventDefault();
    if (id === activeSection) return;
    setActiveSection(id);
    setMobileMenuOpen(false);
  };

  return (
    <PageTransition>
      <SEOHead
        title="智能体概念文档 | OpenClaw 架构、会话、记忆详解"
        description="OpenClaw 智能体架构、会话、记忆、模型等核心概念详解 - 深入理解 AI 智能体工作原理"
        keywords="智能体架构, AI会话管理, 智能体记忆, 模型配置, OpenClaw核心概念"
        canonical={`${siteUrl}/agents`}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-slate-950 flex flex-col">
        <Navbar />

        {/* Mobile Section Selector */}
        <div className="md:hidden sticky top-16 z-40 bg-slate-900 border-b border-slate-800">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${currentNavItem?.icon} text-cyan-400`}></i>
              </div>
              <span className="text-white font-medium text-sm">{currentNavItem?.label}</span>
            </div>
            <i className={`ri-arrow-down-s-line text-slate-400 transition-transform duration-200 ${mobileMenuOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {mobileMenuOpen && (
            <div className="absolute top-full left-0 right-0 bg-slate-900 border-b border-slate-800 max-h-[70vh] overflow-y-auto">
              <nav className="p-3">
                {groups.map((group) => {
                  const items = navItems.filter((n) => n.group === group);
                  if (!items.length) return null;
                  return (
                    <div key={group} className="mb-3">
                      <div className="px-3 py-1.5 text-xs text-slate-500 uppercase tracking-wider font-semibold">
                        {group}
                      </div>
                      {items.map((item) => (
                        <button
                          key={item.id}
                          onClick={(e) => handleSectionChange(e, item.id)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap mb-0.5 text-left ${
                            activeSection === item.id
                              ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                          }`}
                        >
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <i className={`${item.icon} text-base`}></i>
                          </div>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </nav>
            </div>
          )}
        </div>

        <div className="flex flex-1 pt-16" style={{ height: 'calc(100vh - 0px)' }}>
          {/* Main Content */}
          <main
            ref={contentRef}
            className="flex-1 overflow-y-auto"
            style={{ height: 'calc(100vh - 64px)' }}
          >
            <div className="max-w-4xl mx-auto px-8 md:px-16 py-12 min-h-full">
              {contentMap[activeSection]}
            </div>
            <Footer />
          </main>
        </div>
      </div>
    </PageTransition>
  );
}
